import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, PencilSimple, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { saCard, saInput, saLabel, fmtD, SABadge, ACCOUNT_STATUS } from "../../components/saShared";

export default function SARistoranti() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [plans, setPlans] = useState([]);
  const [q, setQ] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null); // {id, name} per la rinomina
  const [form, setForm] = useState({ name: "", referente_name: "", email: "", password: "", phone: "", plan_id: "", comp: false });

  const load = useCallback(() => {
    api.get(`/superadmin/restaurants${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setList(r.data)).catch(() => {});
  }, [q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/superadmin/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const action = async (id, act) => {
    try {
      await api.post(`/superadmin/restaurants/${id}/action`, { action: act });
      toast.success(`Account ${act === "sospendi" ? "sospeso (dati conservati)" : act === "riattiva" ? "riattivato" : "attivato"}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleComp = async (id, comp) => {
    try {
      await api.patch(`/superadmin/restaurants/${id}`, { comp });
      toast.success(comp ? "Account omaggio attivo (gratuito a vita)" : "Omaggio rimosso: account a pagamento");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveRename = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/superadmin/restaurants/${editing.id}`, { name: editing.name.trim() });
      toast.success("Nome ristorante aggiornato");
      setEditing(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/superadmin/restaurants", { ...form, plan_id: form.plan_id || null });
      toast.success("Ristorante creato in prova");
      setShowNew(false);
      setForm({ name: "", referente_name: "", email: "", password: "", phone: "", plan_id: "", comp: false });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="space-y-4" data-testid="sa-ristoranti">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">Ristoranti</h1>
        <button onClick={() => setShowNew(true)} data-testid="sa-new-restaurant"
          className="h-11 px-4 rounded-md bg-amber-400 text-zinc-950 text-sm font-bold flex items-center gap-2 hover:bg-amber-300 active:scale-95 transition">
          <Plus size={16} weight="bold" /> Nuovo ristorante
        </button>
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca ristorante..." data-testid="sa-search"
          className={`${saInput} pl-9`} />
      </div>

      <div className={`${saCard} overflow-x-auto`} data-testid="sa-restaurants-table">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              {["Ristorante", "Referente", "Piano", "Stato account", "Abbonamento", "Attivazione", "Prossimo pagamento", "Ultimo pagamento", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/60">
            {list.map((r) => (
              <tr key={r.id} className="hover:bg-zinc-800/40 transition-colors" data-testid={`sa-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-zinc-100 flex items-center gap-1.5">
                    {r.name}
                    <button onClick={() => setEditing({ id: r.id, name: r.name })} data-testid={`sa-rename-${r.id}`}
                      title="Rinomina ristorante"
                      className="p-1 rounded text-zinc-500 hover:text-amber-300 hover:bg-zinc-800 transition-colors">
                      <PencilSimple size={13} weight="bold" />
                    </button>
                  </div>
                  <div className="text-xs text-zinc-500">{r.phone || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-zinc-200">{r.referente?.name}</div>
                  <div className="text-xs text-zinc-500">{r.referente?.email}</div>
                </td>
                <td className="px-4 py-3 text-zinc-200">{r.plan_name}</td>
                <td className="px-4 py-3"><SABadge map={ACCOUNT_STATUS} value={r.account_status} />
                  {r.comp && <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-400/20 text-amber-300 border border-amber-400/40" data-testid={`sa-comp-badge-${r.id}`}>FREE</span>}
                </td>
                <td className="px-4 py-3 text-xs text-zinc-400 capitalize">{(r.subscription_status || "—").replaceAll("_", " ")}</td>
                <td className="px-4 py-3 text-xs text-zinc-400">{fmtD(r.activated_at)}</td>
                <td className="px-4 py-3 text-xs text-zinc-300">{fmtD(r.next_payment_at)}</td>
                <td className="px-4 py-3 text-xs text-zinc-400">
                  {r.last_payment ? `${fmtD(r.last_payment.created_at)} · €${r.last_payment.amount}` : "—"}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    <button onClick={() => navigate(`/superadmin/ristoranti/${r.id}`)} data-testid={`sa-open-${r.id}`}
                      className="h-8 px-3 rounded-md bg-zinc-800 text-zinc-200 text-xs font-semibold hover:bg-zinc-700">Apri</button>
                    {r.account_status === "trial" && (
                      <button onClick={() => action(r.id, "attiva")} data-testid={`sa-attiva-${r.id}`}
                        className="h-8 px-3 rounded-md bg-emerald-500/20 text-emerald-300 text-xs font-bold border border-emerald-500/40">Attiva</button>
                    )}
                    {["attivo", "trial", "tolleranza", "pagamento_fallito"].includes(r.account_status) && (
                      <button onClick={() => action(r.id, "sospendi")} data-testid={`sa-sospendi-${r.id}`}
                        className="h-8 px-3 rounded-md bg-rose-500/15 text-rose-300 text-xs font-bold border border-rose-500/40">Sospendi</button>
                    )}
                    {r.account_status === "sospeso" && (
                      <button onClick={() => action(r.id, "riattiva")} data-testid={`sa-riattiva-${r.id}`}
                        className="h-8 px-3 rounded-md bg-amber-400/15 text-amber-300 text-xs font-bold border border-amber-400/40">Riattiva</button>
                    )}
                    <button onClick={() => toggleComp(r.id, !r.comp)} data-testid={`sa-comp-${r.id}`}
                      title={r.comp ? "Rimuovi omaggio (torna a pagamento)" : "Imposta omaggio (gratuito a vita)"}
                      className={`h-8 px-3 rounded-md text-xs font-bold border transition ${
                        r.comp ? "bg-amber-400/20 text-amber-300 border-amber-400/40"
                               : "bg-zinc-800 text-zinc-300 border-zinc-700 hover:border-amber-400/40"}`}>
                      {r.comp ? "Omaggio ✓" : "Omaggio"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <div className="py-10 text-center text-sm text-zinc-500">Nessun ristorante</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={() => setEditing(null)}>
          <form onSubmit={saveRename} className={`${saCard} w-full sm:max-w-sm rounded-t-xl sm:rounded-xl p-5 space-y-3`}
            onClick={(e) => e.stopPropagation()} data-testid="sa-rename-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg text-white">Rinomina ristorante</h3>
              <button type="button" onClick={() => setEditing(null)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <div>
              <label className={saLabel}>Nome</label>
              <input required value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                className={saInput} data-testid="sa-rename-input" />
            </div>
            <p className="text-[11px] text-zinc-500">
              Rinomina la scheda interna del ristorante. Il nome visibile ai clienti (menu, pagine pubbliche)
              resta quello di Impostazioni → Generali del ristorante.
            </p>
            <button data-testid="sa-rename-save"
              className="w-full h-11 rounded-md bg-amber-400 text-zinc-950 text-sm font-bold hover:bg-amber-300 active:scale-95 transition">
              Salva nome
            </button>
          </form>
        </div>
      )}

      {showNew && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={() => setShowNew(false)}>
          <form onSubmit={create} className={`${saCard} w-full sm:max-w-md rounded-t-xl sm:rounded-xl p-5 space-y-3 max-h-[90vh] overflow-y-auto`}
            onClick={(e) => e.stopPropagation()} data-testid="sa-new-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg text-white">Nuovo ristorante</h3>
              <button type="button" onClick={() => setShowNew(false)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <div><label className={saLabel}>Nome ristorante *</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={saInput} data-testid="sa-new-name" /></div>
            <div><label className={saLabel}>Referente *</label>
              <input required value={form.referente_name} onChange={(e) => setForm({ ...form, referente_name: e.target.value })} className={saInput} data-testid="sa-new-referente" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className={saLabel}>Email *</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={saInput} data-testid="sa-new-email" /></div>
              <div><label className={saLabel}>Password *</label>
                <input required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={saInput} data-testid="sa-new-password" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className={saLabel}>Telefono</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={saInput} data-testid="sa-new-phone" /></div>
              <div><label className={saLabel}>Piano</label>
                <select value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })} className={saInput} data-testid="sa-new-plan">
                  <option value="">Nessuno</option>
                  {plans.map((p) => <option key={p.id} value={p.id}>{p.name} — €{p.prezzo_mensile}/mese</option>)}
                </select></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={!!form.comp} onChange={(e) => setForm({ ...form, comp: e.target.checked })}
                data-testid="sa-new-comp" className="w-4 h-4 accent-amber-400" />
              Account omaggio (gratuito, nessuna scadenza né trial)
            </label>
            <button className="w-full h-12 rounded-md bg-amber-400 text-zinc-950 font-bold text-sm" data-testid="sa-new-submit">{form.comp ? "Crea account omaggio" : "Crea account (trial)"}</button>
          </form>
        </div>
      )}
    </div>
  );
}
